// Time helpers pinned to the lab's time zone. Cloud Functions run in UTC and
// phones can be anywhere, so every "today" and "8:00 AM" goes through here.
export const TZ = 'America/Chicago';

const fmtCache = new Map();
function fmt(tz) {
  if (!fmtCache.has(tz)) {
    fmtCache.set(tz, new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', weekday: 'short',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }));
  }
  return fmtCache.get(tz);
}
const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** Wall-clock parts of a UTC instant in the lab's zone. */
export function zonedParts(ms, tz = TZ) {
  const p = {};
  for (const { type, value } of fmt(tz).formatToParts(new Date(ms))) p[type] = value;
  return {
    y: +p.year, m: +p.month, d: +p.day, hh: +p.hour, mm: +p.minute, ss: +p.second,
    dow: DOW[p.weekday],
  };
}

/** 'YYYY-MM-DD' for an instant, in the lab's zone. */
export function ymd(ms = Date.now(), tz = TZ) {
  const { y, m, d } = zonedParts(ms, tz);
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Day of week (0 = Sunday) for a 'YYYY-MM-DD' date. */
export function dowOf(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Minutes since local midnight for an instant. */
export function minuteOfDay(ms, tz = TZ) {
  const { hh, mm } = zonedParts(ms, tz);
  return hh * 60 + mm;
}

/** UTC instant for a local wall-clock time ('HH:MM') on a 'YYYY-MM-DD' date. DST-safe. */
export function zonedToMs(dateStr, hhmm, tz = TZ) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [h, mi] = String(hhmm).split(':').map(Number);
  const wall = Date.UTC(y, m - 1, d, h, mi);
  let guess = wall;
  for (let i = 0; i < 3; i++) {
    const p = zonedParts(guess, tz);
    const asWall = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm);
    const diff = wall - asWall;
    if (diff === 0) break;
    guess += diff;
  }
  return guess;
}

export function hm(s) {
  if (s == null) return null;
  const m = String(s).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a|p)?$/i);
  if (!m) return null;
  let h = +m[1];
  const mi = +(m[2] || 0);
  const ap = (m[3] || '').toLowerCase();
  if (ap.startsWith('p') && h < 12) h += 12;
  if (ap.startsWith('a') && h === 12) h = 0;
  if (h > 24 || mi > 59) return null;
  return h * 60 + mi;
}

export function toHM(min) {
  return String(Math.floor(min / 60)).padStart(2, '0') + ':' + String(min % 60).padStart(2, '0');
}

/** '9:05 AM' from minutes since midnight. */
export function fmtMin(min) {
  min = Math.round(min);
  let h = Math.floor(min / 60) % 24;
  const m = ((min % 60) + 60) % 60;
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')} ${ap}`;
}

/** '9:05 AM' for an instant, in the lab's zone. */
export function fmtTime(ms, tz = TZ) {
  if (!ms) return '';
  return fmtMin(minuteOfDay(ms, tz));
}
